import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const PILLARS = [
  { title: "Heterosis", body: "Cross two unrelated breeds and the calf outperforms both parents. Bred in on day one — no extra cost." },
  { title: "Breed Complementarity", body: "Angus brings marbling and marketing power. Simmental brings growth, muscle and milk. The cross gets both." },
  { title: "Maternal Advantage", body: "Keep the F1 heifers and you've built the half-blood cow — more fertile, longer-lived, heavier-weaning." },
  { title: "Added Pounds", body: "More live calves, more calves weaned, more pounds per calf. It shows up on the scale first." },
  { title: "Carcass Merit", body: "F1 calves feed efficiently and hang carcasses with both yield and marbling — the kind feeders ask for." },
];

export default function HeterosisPage() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "The Power of Heterosis — Bulls of the Big Sky";
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="heterosis-page" className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <SiteHeader />

      {/* HERO */}
      <section className="pt-40 pb-16 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="eyebrow">Crossbreeding, Done Right</div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mt-4 leading-[0.95]">
            The power of heterosis.
          </h1>
          <p className="font-italic-serif text-2xl md:text-4xl mt-4" style={{ color: "var(--brass-deep)" }}>
            Why Simmental × Angus works
          </p>
        </div>
      </section>

      {/* CROSS DIAGRAM */}
      <section data-testid="cross-diagram" className="px-6 md:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="section-deep p-8 md:p-14">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center text-center">
              <div className="border p-6 md:p-8" style={{ borderColor: "rgba(191,212,224,0.35)" }}>
                <div className="eyebrow text-[10px]" style={{ color: "var(--sky-pale)" }}>Sire</div>
                <div className="font-display text-2xl md:text-3xl mt-2 leading-tight" style={{ color: "var(--surface)" }}>
                  Purebred<br />Simmental Bull
                </div>
              </div>
              <div className="font-display text-5xl md:text-6xl" style={{ color: "var(--hay)" }}>×</div>
              <div className="border p-6 md:p-8" style={{ borderColor: "rgba(191,212,224,0.35)" }}>
                <div className="eyebrow text-[10px]" style={{ color: "var(--sky-pale)" }}>Dam</div>
                <div className="font-display text-2xl md:text-3xl mt-2 leading-tight" style={{ color: "var(--surface)" }}>
                  Angus<br />Cow
                </div>
              </div>
              <div className="font-display text-5xl md:text-6xl" style={{ color: "var(--hay)" }}>=</div>
              <div className="p-6 md:p-8" style={{ background: "var(--hay)" }}>
                <div className="eyebrow text-[10px]" style={{ color: "#0E2C46" }}>The Result</div>
                <div className="font-display text-2xl md:text-3xl mt-2 leading-tight" style={{ color: "#0E2C46" }}>
                  F1 Simmental<br />× Angus Calf
                </div>
              </div>
            </div>
            <p className="mt-8 text-center text-sm md:text-base max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.75)" }}>
              The first cross captures <strong style={{ color: "var(--hay)" }}>maximum heterosis</strong> —
              no other mating on an Angus cow gets you more.
            </p>
          </div>
        </div>
      </section>

      {/* FREE LUNCH QUOTE */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto border-l-4 pl-8 md:pl-12 py-4" style={{ borderColor: "var(--brass-deep)" }}>
          <blockquote className="font-display text-3xl md:text-5xl leading-[1.1]" style={{ color: "var(--text-primary)" }}>
            &ldquo;Heterosis is the only free lunch in the cattle business.&rdquo;
          </blockquote>
          <div className="eyebrow mt-4 text-[11px]">Bulls of the Big Sky Sale Book</div>
        </div>
      </section>

      {/* THE NUMBERS */}
      <section data-testid="heterosis-numbers" className="px-6 md:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="eyebrow mb-4">What the Research Shows</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="border p-8" style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
              <div className="font-display text-6xl" style={{ color: "var(--brass-deep)" }}>+20%</div>
              <div className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                More calves over a lifetime from crossbred cows vs. straightbreds. (U.S. Meat Animal Research Center)
              </div>
            </div>
            <div className="border p-8" style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
              <div className="font-display text-6xl" style={{ color: "var(--brass-deep)" }}>More lbs</div>
              <div className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                Substantially greater lifetime weight weaned — pounds compounding year after year.
              </div>
            </div>
            <div className="border p-8" style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
              <div className="font-display text-6xl" style={{ color: "var(--brass-deep)" }}>Both</div>
              <div className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                Growth <em>and</em> carcass merit in the same calf.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section data-testid="heterosis-pillars" className="px-6 md:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="eyebrow mb-4">Why It Works</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="border p-8" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-secondary)" }}>
                <div className="eyebrow text-[10px]" style={{ color: "var(--brass-bright)" }}>{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-2">{p.title}</h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PUREBRED VS SIMANGUS */}
      <section data-testid="purebred-vs-simangus" className="px-6 md:px-12 pb-24">
        <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="eyebrow mb-4">Choosing Your Bull</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Purebred Simmental or SimAngus?</h2>
          <p className="mt-6 max-w-3xl text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
            Both are right — for different cow herds. We sell both.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            <div className="border p-8 md:p-10" style={{ borderColor: "var(--brass-deep)", background: "var(--surface)" }}>
              <h3 className="font-display text-3xl">Purebred Simmental Bull</h3>
              <div className="eyebrow text-[10px] mt-2" style={{ color: "var(--brass-bright)" }}>On Angus-Based Cows</div>
              <ul className="mt-6 space-y-4 text-sm md:text-base" style={{ color: "var(--text-secondary)" }}>
                <li>• <strong style={{ color: "var(--text-primary)" }}>Maximum heterosis</strong> in the F1 calf.</li>
                <li>• Biggest one-generation infusion of growth, muscle and milk.</li>
                <li>• F1 heifers make the famous half-blood cow.</li>
              </ul>
            </div>
            <div className="border p-8 md:p-10" style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
              <h3 className="font-display text-3xl">SimAngus Bull</h3>
              <div className="eyebrow text-[10px] mt-2" style={{ color: "var(--brass-bright)" }}>On Mixed or Crossbred Cows</div>
              <ul className="mt-6 space-y-4 text-sm md:text-base" style={{ color: "var(--text-secondary)" }}>
                <li>• <strong style={{ color: "var(--text-primary)" }}>Simplified crossbreeding</strong> — one bull, no rotation.</li>
                <li>• Retains hybrid vigor in Sim-influenced herds.</li>
                <li>• Smooths and moderates a mixed calf crop.</li>
              </ul>
            </div>
          </div>
          <p className="mt-10 max-w-3xl text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
            Know your cows, know your goal — and if you want help sorting it out, call the sale office.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row flex-wrap gap-4">
            <Link to="/sale/annual" data-testid="heterosis-cta-annual" className="btn-primary">
              See the Annual Sale bulls →
            </Link>
            <Link to="/sale/legacy" data-testid="heterosis-cta-legacy" className="btn-secondary">
              See the Legacy Sale →
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter settings={settings} />
    </div>
  );
}
